/*
 * Copyright 2026 Sony Corporation
 */
import type { Roadmap, RoadmapActionRequest, RoadmapStep } from '@ips-jit/schema';
import { Box, Button, ButtonBase, Chip, CircularProgress, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { useEffect, useState } from 'react';

import {
  clock,
  detailOf,
  iconOf,
  progressOf,
  remainingOf,
  shownSteps,
  STAGES,
  statusOf,
  type StepIcon,
} from '../services/roadmap';

import { FoldButton } from './FoldedStrip';

const COLOR: Record<Exclude<StepIcon, 'active'>, string> = {
  done: 'success.main',
  pending: 'text.disabled',
  failed: 'error.main',
  skipped: 'text.disabled',
  removed: 'text.disabled',
};

const SYMBOL: Record<Exclude<StepIcon, 'active'>, string> = {
  done: '✓',
  pending: '',
  failed: '!',
  skipped: '–',
  removed: '–',
};

const LABEL: Record<StepIcon, string> = {
  done: 'done',
  active: 'in progress',
  pending: 'planned',
  failed: 'failed',
  skipped: 'skipped',
  removed: 'removed',
};

const StateIcon = ({ icon }: { icon: StepIcon }) =>
  icon === 'active' ? (
    <CircularProgress size={18} thickness={5} aria-label={LABEL.active} />
  ) : (
    <Box
      aria-label={LABEL[icon]}
      role="img"
      sx={{
        width: 18,
        height: 18,
        borderRadius: '50%',
        flexShrink: 0,
        display: 'grid',
        placeItems: 'center',
        fontSize: 12,
        lineHeight: 1,
        border: 2,
        borderColor: COLOR[icon],
        ...(icon === 'done' || icon === 'failed' ? { bgcolor: COLOR[icon], color: 'common.black' } : {}),
        ...(icon === 'skipped' || icon === 'removed' ? { color: COLOR[icon] } : {}),
      }}
    >
      {SYMBOL[icon]}
    </Box>
  );

/** Where a running step is: the stages before it done, its own in progress, the rest to come. */
const Stages = ({ step }: { step: RoadmapStep }) => {
  const at = STAGES.findIndex((s) => s.id === (step.stage ?? 'writing'));
  return (
    <Typography variant="caption" color="text.secondary" component="p" aria-label="Stage">
      {STAGES.map((stage, i) => (
        <Box
          component="span"
          key={stage.id}
          sx={{
            color: i === at ? 'info.light' : i < at ? 'success.light' : 'text.disabled',
            fontWeight: i === at ? 700 : 400,
          }}
        >
          {i > 0 ? ' → ' : ''}
          {stage.label}
          {i < at ? ' ✓' : ''}
        </Box>
      ))}
    </Typography>
  );
};

export interface RoadmapPanelProps {
  roadmap: Roadmap | null;
  /** The newest thing the assistant said it was doing, shown under the running step. */
  step?: string;
  onAction: (action: RoadmapActionRequest) => Promise<void>;
  /** Picks the version a step produced, to show it in the App card. */
  onSelectVersion?: (version: number) => void;
  /** Called when the user opens a step, so the conversation can show where it was talked about. */
  onSelectStep?: (stepId: string) => void;
  /** Folds the column the panel is in. */
  onFold?: () => void;
}

/**
 * The steps the app is built in and where each one stands, updated as the steps run and as the user's messages revise
 * them. One line a step; what a step brings and how it went shows for the running, failed or just revised one, and
 * for any other the user opens.
 */
export const RoadmapPanel = ({
  roadmap,
  step: latest,
  onAction,
  onSelectVersion,
  onSelectStep,
  onFold,
}: RoadmapPanelProps) => {
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const [opened, setOpened] = useState<ReadonlySet<string>>(new Set());
  const running = roadmap?.steps.some((s) => s.state === 'running') ?? false;

  useEffect(() => {
    if (!running) {
      return;
    }
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [running]);

  const act = async (action: RoadmapActionRequest) => {
    setBusy(true);
    try {
      await onAction(action);
    } finally {
      setBusy(false);
    }
  };

  const toggle = (stepId: string) => {
    const next = new Set(opened);
    if (next.has(stepId)) {
      next.delete(stepId);
    } else {
      next.add(stepId);
      onSelectStep?.(stepId);
    }
    setOpened(next);
  };

  const steps = roadmap ? shownSteps(roadmap) : [];
  const status = roadmap ? statusOf(roadmap) : undefined;
  const left = roadmap && ['running', 'paused'].includes(roadmap.status) ? remainingOf(roadmap, now) : 0;
  const revised = roadmap && roadmap.revision > 1 ? roadmap.steps.filter((s) => s.revised_in === roadmap.revision) : [];

  return (
    <Paper
      variant="outlined"
      component="section"
      aria-label="Roadmap"
      sx={{ p: 2, display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}
    >
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Typography variant="subtitle1" noWrap sx={{ flex: 1 }}>
          Roadmap{roadmap ? ` (${progressOf(roadmap)})` : ''}
        </Typography>
        {roadmap?.status === 'running' && (
          <Button size="small" disabled={busy} onClick={() => void act({ action: 'pause' })}>
            Pause
          </Button>
        )}
        {roadmap?.status === 'paused' && !roadmap.steps.some((s) => s.state === 'failed') && (
          <Button size="small" disabled={busy} onClick={() => void act({ action: 'resume' })}>
            Resume
          </Button>
        )}
        {onFold && <FoldButton label="Roadmap" side="left" onFold={onFold} />}
      </Stack>
      {!roadmap && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          Describe your app in the chat. The assistant breaks it into steps you can try on the device one after another;
          they appear here.
        </Typography>
      )}
      {(status || left > 0) && (
        <Typography variant="caption" color="text.secondary" component="p">
          {[status, left > 0 ? `about ${clock(left * 1000)} left` : ''].filter(Boolean).join(' ')}
        </Typography>
      )}
      <Box
        aria-live="polite"
        sx={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
          whiteSpace: 'nowrap',
        }}
      >
        {revised.length > 0 ? `The roadmap changed: ${revised.map((s) => `${s.change} ${s.title}`).join(', ')}.` : ''}
      </Box>
      <Stack
        component="ol"
        spacing={1}
        sx={{ listStyle: 'none', p: 0, m: 0, mt: 1.5, minHeight: 0, overflowY: 'auto', mr: -1, pr: 1 }}
      >
        {steps.map((step, i) => {
          const icon = iconOf(step);
          const isRevised = step.change !== null && step.revised_in === roadmap?.revision;
          const open = step.state === 'running' || step.state === 'failed' || isRevised || opened.has(step.step_id);
          return (
            <Stack
              component="li"
              key={step.step_id}
              direction="row"
              spacing={1}
              sx={{ alignItems: 'flex-start' }}
              aria-current={step.state === 'running' ? 'step' : undefined}
            >
              <Box sx={{ pt: 0.25 }}>
                <StateIcon icon={icon} />
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                  <ButtonBase
                    aria-expanded={open}
                    onClick={() => toggle(step.step_id)}
                    sx={{ flex: 1, minWidth: 0, justifyContent: 'flex-start', textAlign: 'left', borderRadius: 0.5 }}
                  >
                    <Typography
                      variant="body2"
                      noWrap={!open}
                      color={
                        icon === 'pending' || icon === 'skipped' || icon === 'removed'
                          ? 'text.secondary'
                          : 'text.primary'
                      }
                      sx={{
                        fontWeight: step.state === 'running' ? 700 : 400,
                        textDecoration: icon === 'removed' ? 'line-through' : undefined,
                        minWidth: 0,
                      }}
                    >
                      {step.state === 'removed' ? '' : `${i + 1}. `}
                      {step.title}
                    </Typography>
                  </ButtonBase>
                  {isRevised && step.change !== 'removed' && (
                    <Chip
                      size="small"
                      variant="outlined"
                      color={step.change === 'added' ? 'success' : 'info'}
                      label={step.change === 'added' ? 'Added' : 'Changed'}
                    />
                  )}
                  {step.version !== null && (
                    <Tooltip title={`Show version ${step.version} in the App card`}>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`v${step.version}`}
                        aria-label={`Version ${step.version}`}
                        onClick={onSelectVersion ? () => onSelectVersion(step.version as number) : undefined}
                        sx={{ height: 20, '& .MuiChip-label': { px: 0.75 } }}
                      />
                    </Tooltip>
                  )}
                </Stack>
                {open && (
                  <>
                    {step.state !== 'removed' && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        component="p"
                        sx={{ overflowWrap: 'anywhere' }}
                      >
                        {step.outcome}
                      </Typography>
                    )}
                    <Typography
                      variant="caption"
                      color={step.state === 'failed' ? 'error.light' : 'text.secondary'}
                      component="p"
                    >
                      {detailOf(step, now)}
                    </Typography>
                    {step.state === 'running' && (
                      <>
                        <Stages step={step} />
                        {latest && (
                          <Typography variant="caption" color="text.disabled" component="p" noWrap>
                            {latest}
                          </Typography>
                        )}
                      </>
                    )}
                    {step.state === 'failed' && (
                      <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={busy}
                          onClick={() => void act({ action: 'retry', step_id: step.step_id })}
                        >
                          Try again
                        </Button>
                        <Button
                          size="small"
                          disabled={busy}
                          onClick={() => void act({ action: 'skip', step_id: step.step_id })}
                        >
                          Skip
                        </Button>
                      </Stack>
                    )}
                  </>
                )}
              </Box>
            </Stack>
          );
        })}
      </Stack>
    </Paper>
  );
};
